import { LoadingNote } from "../lib/Loading.tsx";
import { WORK_IMAGE_COPY } from "./work.constants.ts";
import type { WorkImagesModel } from "./work.model.ts";

/**
 * The photographs of one work: the chosen view, and every view under it.
 *
 * The captions are the record speaking, not decoration — 'Back / verso' under a
 * picture is the collector's own statement of what it shows, which is why the
 * strip labels every tile rather than only the selected one.
 *
 * Fitted rather than filled. A registry photograph cropped to a tidy rectangle
 * is a photograph with part of the work outside the frame, and this is the one
 * screen where the whole of it matters.
 */
export function WorkImages({
  images,
  onSelect,
}: {
  images: WorkImagesModel;
  onSelect: (imageId: string) => void;
}) {
  if (images.kind === "loading") {
    return (
      <section className="work__images">
        <LoadingNote message={images.message} />
      </section>
    );
  }

  if (images.kind === "failed" || images.kind === "empty") {
    return (
      <section className="work__images">
        <div className="work__hero work__hero--blank">
          <p className="work__images-note">{images.message}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="work__images" aria-label={WORK_IMAGE_COPY.galleryLabel}>
      <div className="work__hero">
        {images.hero.url === null ? (
          <p className="work__images-note">{WORK_IMAGE_COPY.unreadable}</p>
        ) : (
          <img className="work__hero-img" src={images.hero.url} alt={images.hero.alt} />
        )}
        <span className="work__hero-caption">{images.hero.caption}</span>
      </div>

      {images.thumbs.length > 1 && (
        <ul className="work__thumbs">
          {images.thumbs.map((thumb) => (
            <li key={thumb.id}>
              <button
                type="button"
                className={`work__thumb${thumb.selected ? " work__thumb--on" : ""}`}
                aria-pressed={thumb.selected}
                onClick={() => onSelect(thumb.id)}
              >
                {thumb.url === null ? (
                  <span className="work__thumb-blank" aria-hidden="true" />
                ) : (
                  <img className="work__thumb-img" src={thumb.url} alt={thumb.alt} />
                )}
                <span className="work__thumb-caption">{thumb.caption}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
